import moment from 'moment-timezone';
import { buildAccountKey } from '@sales-portal/domain/pricing/blocs/utils/account-key';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  EditScope,
  EditScopeKind,
  PricingEdit,
  PricingEditsState,
  PricingEditsStore,
} from './pricing-edits-store-types';
import {
  experienceStorage,
  STORAGE_KEYS,
} from '@experiences/blocs/utils/storage';
import {
  popSnapshot,
  pushSnapshot,
} from '@/experiences/sales-portal/blocs/utils/snapshot-undo-stack';

export const REVIEW_SCOPE: EditScope = { kind: EditScopeKind.Review };

export const BOOKMARKED_SCOPE: EditScope = { kind: EditScopeKind.Bookmarked };

export const variantPackScope = (uuid: string): EditScope => ({
  kind: EditScopeKind.VariantPack,
  uuid,
});

export const restaurantScope = (uuid: string): EditScope => ({
  kind: EditScopeKind.Restaurant,
  uuid,
});

export const scopeStorageKey = (scope: EditScope): string =>
  scope.kind === EditScopeKind.Review || scope.kind === EditScopeKind.Bookmarked
    ? scope.kind
    : `${scope.kind}:${scope.uuid}`;

const mergeEdits = (
  current: PricingEdit[],
  incoming: PricingEdit[],
): PricingEdit[] => {
  const editMap = new Map<string, PricingEdit>();

  for (const edit of current) {
    editMap.set(buildAccountKey(edit), edit);
  }

  for (const edit of incoming) {
    editMap.set(buildAccountKey(edit), edit);
  }

  return [...editMap.values()];
};

const matchesScope = (edit: PricingEdit, scope: EditScope): boolean => {
  switch (scope.kind) {
    case EditScopeKind.Review:
    case EditScopeKind.Bookmarked:
      return true;
    case EditScopeKind.VariantPack:
      return edit.variantPackUUID === scope.uuid;
    case EditScopeKind.Restaurant:
      return edit.restaurantUUID === scope.uuid;
  }
};

/** Returns only the edits that belong to the given scope. */
const editsForScope = (edits: PricingEdit[], scope: EditScope): PricingEdit[] =>
  edits.filter((e) => matchesScope(e, scope));

/** Returns the edits that do NOT belong to the given scope. */
const editsOutsideScope = (
  edits: PricingEdit[],
  scope: EditScope,
): PricingEdit[] =>
  scope.kind === EditScopeKind.Review || scope.kind === EditScopeKind.Bookmarked
    ? []
    : edits.filter((e) => !matchesScope(e, scope));

/** Pushes a scoped edit snapshot onto the undo stack for the given scope. */
const pushToScope = (
  stackMap: Record<string, PricingEdit[][]>,
  scope: EditScope,
  allEdits: PricingEdit[],
): Record<string, PricingEdit[][]> => {
  const key = scopeStorageKey(scope);
  return {
    ...stackMap,
    [key]: pushSnapshot(stackMap[key] ?? [], editsForScope(allEdits, scope)),
  };
};

const calculateValidUntil = (timezone: string): string => {
  const now = moment().tz(timezone);
  const threeAmToday = moment().tz(timezone).startOf('day').add(3, 'hours');

  // If it's already past 3 AM today, it expires at 3 AM tomorrow.
  // If it's before 3 AM today, it expires at 3 AM today.
  return now.isSameOrAfter(threeAmToday)
    ? threeAmToday.clone().add(1, 'day').toISOString()
    : threeAmToday.toISOString();
};

const EMPTY_STATE = {
  edits: [],
  editSnapshotStack: {} as Record<string, PricingEdit[][]>,
  expiredEditsCount: 0,
  initialEdits: [],
  validUntil: undefined,
};

export const usePricingEditsStore = create<PricingEditsStore>()(
  persist(
    (set) => ({
      ...EMPTY_STATE,

      applyEdits: (incoming, scope) =>
        set((state) => ({
          edits: mergeEdits(state.edits, incoming),
          editSnapshotStack: pushToScope(
            state.editSnapshotStack,
            scope,
            state.edits,
          ),
          supplierTimezone: state.supplierTimezone,
          validUntil: calculateValidUntil(state.supplierTimezone),
        })),

      resetUndoHistory: (scope) =>
        set((state) => {
          const key = scopeStorageKey(scope);
          if (!(key in state.editSnapshotStack)) {
            return state;
          }
          const editSnapshotStack = { ...state.editSnapshotStack };
          delete editSnapshotStack[key];
          return { editSnapshotStack };
        }),

      revertAllEdits: () =>
        set((state) => ({
          ...EMPTY_STATE,
          supplierTimezone: state.supplierTimezone,
        })),

      revertEditsByRestaurant: (restaurantUUID, variantPackUUIDs, scope) =>
        set((state) => {
          if (variantPackUUIDs.length === 0) {
            return state;
          }

          const variantPackUUIDSet = new Set(variantPackUUIDs);
          const updatedEdits = state.edits.filter(
            (e) =>
              e.restaurantUUID !== restaurantUUID ||
              !variantPackUUIDSet.has(e.variantPackUUID),
          );
          return {
            edits: updatedEdits,
            editSnapshotStack: pushToScope(
              state.editSnapshotStack,
              scope,
              state.edits,
            ),
            supplierTimezone: state.supplierTimezone,
            validUntil:
              updatedEdits.length > 0
                ? calculateValidUntil(state.supplierTimezone)
                : undefined,
          };
        }),

      revertEditsByVariantPack: (variantPackUUID, restaurantUUIDs, scope) =>
        set((state) => {
          if (restaurantUUIDs.length === 0) {
            return state;
          }

          const restaurantUUIDSet = new Set(restaurantUUIDs);
          const updatedEdits = state.edits.filter(
            (e) =>
              e.variantPackUUID !== variantPackUUID ||
              !restaurantUUIDSet.has(e.restaurantUUID),
          );
          return {
            edits: updatedEdits,
            editSnapshotStack: pushToScope(
              state.editSnapshotStack,
              scope,
              state.edits,
            ),
            supplierTimezone: state.supplierTimezone,
            validUntil:
              updatedEdits.length > 0
                ? calculateValidUntil(state.supplierTimezone)
                : undefined,
          };
        }),

      setSupplierTimezone: (timezone: string) =>
        set({ supplierTimezone: timezone }),

      supplierTimezone: moment.tz.guess(),

      undo: (scope) =>
        set((state) => {
          const key = scopeStorageKey(scope);
          const { previous, remaining } = popSnapshot(
            state.editSnapshotStack[key] ?? [],
          );
          if (!previous) {
            return state;
          }
          const otherEdits = editsOutsideScope(state.edits, scope);
          const edits = mergeEdits(otherEdits, previous);
          return {
            edits,
            editSnapshotStack: {
              ...state.editSnapshotStack,
              [key]: remaining,
            },
            supplierTimezone: state.supplierTimezone,
            validUntil:
              edits.length > 0
                ? calculateValidUntil(state.supplierTimezone)
                : undefined,
          };
        }),
    }),
    {
      merge: (persisted, current) => {
        // `persisted` is null when nothing is stored yet. Destructuring null
        // throws, which zustand swallows and leaves the store permanently
        // un-hydrated (hasHydrated() stuck false, onFinishHydration never
        // fires) — so default to an empty object.
        const { edits = [], validUntil } = (persisted ?? {}) as {
          edits?: PricingEdit[];
          validUntil?: string;
        };

        // If the current time has passed the validUntil threshold, flush.
        if (validUntil && moment().isSameOrAfter(moment(validUntil))) {
          return {
            ...current,
            ...EMPTY_STATE,
            expiredEditsCount: edits.length,
          };
        }

        return {
          ...current,
          edits,
          editSnapshotStack: {},
          initialEdits: edits,
          validUntil,
        };
      },
      name: STORAGE_KEYS.PRICING_EDITS,
      partialize: (state) => ({
        edits: state.edits,
        validUntil: state.validUntil,
      }),
      storage: createJSONStorage(() => ({
        getItem: (key) => experienceStorage.getString(key) ?? null,
        removeItem: (key) => experienceStorage.remove(key),
        setItem: (key, value) => experienceStorage.set(key, value),
      })),
    },
  ),
);

export const selectPriceEdit = (
  state: PricingEditsState,
  variantPackUUID: string,
  restaurantUUID: string,
): PricingEdit | undefined =>
  state.edits.find(
    (e) =>
      e.variantPackUUID === variantPackUUID &&
      e.restaurantUUID === restaurantUUID,
  );

export const selectVariantPackEditsCount = (
  state: PricingEditsState,
  variantPackUUID: string,
): number =>
  state.edits.filter((e) => e.variantPackUUID === variantPackUUID).length;

export const selectCustomerEditsCount = (
  state: PricingEditsState,
  restaurantUUID: string,
): number =>
  state.edits.filter((e) => e.restaurantUUID === restaurantUUID).length;
